import { useMemo } from 'react';
import { useOutcomes } from '../../../../api/strategy-client/strategy-client.hooks';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../../helpers/feature-flags/feature-flags.type';
import { useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import {
  setIsActiveFilters,
  setParentOutcomesFilters,
  setSourceFilters,
  setStateFilters,
  setStatusFilters,
} from '../../../../store/strategy-store/strategy-store.actions';
import {
  getActiveStateOptions,
  getOutcomeOptions,
  getSourceOptions,
  getStateOptions,
  getStatusOptions,
} from './filters.helpers';
import type { FilterGroup } from './filters.type';

/**
 * Custom hook that returns an array of filter groups for the strategy view
 * Each filter group contains configuration for either status or source filtering
 *
 * The status filter group allows filtering by item status (In Progress, Not Active, etc)
 * The source filter group allows filtering by data source (Bloomfilter, Jira, etc)
 * The parent outcomes filter group allows filtering by parent outcomes (if corporate goals is enabled)
 *
 * All groups support:
 * - Multiple selection
 * - Show all option
 * - Material icon
 * - Custom labels
 *
 * @returns {FilterGroup[]} Array of filter group configurations with current selected values and change handlers
 */
const useFilterGroups = (): FilterGroup[] => {
  const i18n = useTranslation();
  const statusFilters = useStrategyStore((state) => state.filters.status || []);
  const sourceFilters = useStrategyStore((state) => state.filters.source || []);
  const stateFilters = useStrategyStore((state) => state.filters.state || []);
  const isActiveFilters = useStrategyStore((state) => state.filters.is_active || []);
  const parentOutcomesFilters = useStrategyStore((state) => state.filters.parent_outcomes || []);

  const { outcomes } = useOutcomes();
  const showParentOutcomes = useFeatureFlag(FeatureFlag.CorporateGoals);

  return useMemo(() => {
    const filterGroups: FilterGroup[] = [
      {
        id: 'status',
        name: i18n.t('strategy.filters.status'),
        icon: 'filter_tilt_shift',
        options: getStatusOptions(i18n.t),
        selectedValues: statusFilters,
        onSelectionChange: setStatusFilters,
        showAllLabel: i18n.t('strategy.filters.all_statuses'),
      },
      {
        id: 'state',
        name: i18n.t('strategy.filters.state'),
        icon: 'flare',
        options: getStateOptions(i18n.t),
        selectedValues: stateFilters,
        onSelectionChange: setStateFilters,
        showAll: false,
      },
      {
        id: 'is_active',
        name: i18n.t('strategy.filters.activity'),
        icon: 'toggle_on',
        options: getActiveStateOptions(i18n.t),
        selectedValues: isActiveFilters,
        onSelectionChange: setIsActiveFilters,
        showAllLabel: i18n.t('strategy.filters.active_inactive'),
      },
      {
        id: 'source',
        name: i18n.t('strategy.filters.source'),
        icon: 'cloud_done',
        options: getSourceOptions(),
        selectedValues: sourceFilters,
        onSelectionChange: setSourceFilters,
        showAllLabel: i18n.t('strategy.filters.all_sources'),
      },
    ];

    // Add parent outcomes filter group if corporate goals feature flag is enabled
    if (showParentOutcomes && outcomes.length > 0) {
      filterGroups.push({
        id: 'parent_outcomes',
        name: i18n.t('strategy.filters.parent_outcomes'),
        icon: 'checklist',
        options: getOutcomeOptions(outcomes),
        selectedValues: parentOutcomesFilters,
        onSelectionChange: setParentOutcomesFilters,
        showAllLabel: i18n.t('strategy.filters.all_outcomes'),
      });
    }

    return filterGroups;
  }, [
    statusFilters,
    sourceFilters,
    stateFilters,
    isActiveFilters,
    parentOutcomesFilters,
    showParentOutcomes,
    outcomes,
    i18n.t,
  ]);
};

export { useFilterGroups };
